import normalizeUrl from "@/utils/normalizeUrl";
import { KeyboardArrowDownOutlined, VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Link, Stack, Tooltip, Typography } from "@mui/joy";
import { Fragment, useState } from "react";
import { ProfileField } from "../profileFields";
import CopyButton from "./CopyButton";

type FieldGridProps = {
  fields: ProfileField[];
  // search term to highlight; when set, empty fields are listed too
  highlight?: string;
  columns?: 1 | 2;
};

const maskValue = (value: string) => "•".repeat(Math.max(value.length - 3, 4)) + value.slice(-3);

function Highlight({ text, query }: { text: string; query?: string }) {
  const needle = query?.trim().toLowerCase();
  if (!needle) return <>{text}</>;

  const parts: { text: string; match: boolean }[] = [];
  const haystack = text.toLowerCase();
  let cursor = 0;
  let index = haystack.indexOf(needle);
  while (index !== -1) {
    if (index > cursor) parts.push({ text: text.slice(cursor, index), match: false });
    parts.push({ text: text.slice(index, index + needle.length), match: true });
    cursor = index + needle.length;
    index = haystack.indexOf(needle, cursor);
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false });

  return (
    <>
      {parts.map((part, i) =>
        part.match ? (
          <Box
            key={i}
            component="mark"
            sx={{ backgroundColor: "warning.softBg", color: "inherit", borderRadius: "2px", px: "1px" }}
          >
            {part.text}
          </Box>
        ) : (
          <Fragment key={i}>{part.text}</Fragment>
        ),
      )}
    </>
  );
}

function SecretValue({ value }: { value: string }) {
  const [revealed, setRevealed] = useState(false);

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Typography level="body-md" sx={{ fontFamily: revealed ? undefined : "monospace", letterSpacing: "0.04em" }}>
        {revealed ? value : maskValue(value)}
      </Typography>
      <Tooltip title={revealed ? "Hide" : "Reveal"} placement="top" size="sm">
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          aria-label={revealed ? "Hide value" : "Reveal value"}
          onClick={() => setRevealed((prev) => !prev)}
          sx={{ "--IconButton-size": "26px", "--Icon-fontSize": "15px" }}
        >
          {revealed ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
        </IconButton>
      </Tooltip>
      {revealed && <CopyButton value={value} />}
    </Stack>
  );
}

export function FieldValue({ field, highlight }: { field: ProfileField; highlight?: string }) {
  const { value, kind } = field;

  if (!value) {
    return (
      <Typography level="body-md" textColor="text.tertiary">
        —
      </Typography>
    );
  }

  if (kind === "secret") return <SecretValue value={value} />;

  const normalizedUrl = kind === "url" ? normalizeUrl(value) : null;
  const href =
    kind === "email"
      ? `mailto:${value}`
      : kind === "phone"
      ? `tel:${value.replace(/[^\d+]/g, "")}`
      : normalizedUrl && /^https?:\/\//i.test(normalizedUrl)
      ? normalizedUrl
      : null;

  return (
    // copy button only appears on hover/focus so rows stay calm
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      sx={{
        minWidth: 0,
        "& .field-copy": { opacity: 0, transition: "opacity 120ms" },
        "&:hover .field-copy, & .field-copy:focus-within": { opacity: 1 },
      }}
    >
      {href ? (
        <Link
          href={href}
          level="body-md"
          target={kind === "url" ? "_blank" : undefined}
          rel={kind === "url" ? "noopener noreferrer" : undefined}
          sx={{ wordBreak: "break-all" }}
        >
          <Highlight text={value} query={highlight} />
        </Link>
      ) : (
        <Typography level="body-md" textColor="text.primary" sx={{ wordBreak: "break-word", whiteSpace: "pre-line" }}>
          <Highlight text={value} query={highlight} />
        </Typography>
      )}
      <Box className="field-copy">
        <CopyButton value={value} />
      </Box>
    </Stack>
  );
}

function FieldGrid({ fields, highlight, columns = 2 }: FieldGridProps) {
  const [showEmpty, setShowEmpty] = useState(false);

  const filledFields = fields.filter((f) => f.value);
  const emptyCount = fields.length - filledFields.length;
  const visibleFields = highlight || showEmpty ? fields : filledFields;

  return (
    <Stack gap={1.5}>
      {visibleFields.length > 0 ? (
        <Box
          component="dl"
          sx={{
            m: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: columns === 2 ? "repeat(2, minmax(0, 1fr))" : "1fr" },
            columnGap: 3,
            rowGap: 2,
          }}
        >
          {visibleFields.map((f) => (
            <Box key={f.label} sx={{ gridColumn: f.fullWidth ? "1 / -1" : undefined, minWidth: 0 }}>
              <Typography component="dt" level="body-xs" textColor="text.tertiary" fontWeight="md" sx={{ mb: 0.25 }}>
                <Highlight text={f.label} query={highlight} />
              </Typography>
              <Box component="dd" sx={{ m: 0 }}>
                <FieldValue field={f} highlight={highlight} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography level="body-sm" textColor="text.tertiary">
          Nothing filled in yet.
        </Typography>
      )}

      {!highlight && emptyCount > 0 && (
        <Button
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => setShowEmpty((prev) => !prev)}
          endDecorator={
            <KeyboardArrowDownOutlined
              sx={{ transform: showEmpty ? "rotate(180deg)" : "none", transition: "transform 150ms" }}
            />
          }
          sx={{ alignSelf: "flex-start", px: 1, ml: -1, color: "text.tertiary", fontWeight: "md" }}
        >
          {showEmpty ? "Hide empty fields" : `Show ${emptyCount} empty field${emptyCount === 1 ? "" : "s"}`}
        </Button>
      )}
    </Stack>
  );
}

export default FieldGrid;
